import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import SaveIcon from '@mui/icons-material/Save';
import { Box, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetSettingsForRelationRawQuery, useUpdateSettingsForRelationMutation } from "../../../../../newapi/global/generalApiSlice";
import { tokens } from "../../../../../theme";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../../theme/buttons/Shbutton";
import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import SidepanelHeaderActiongroup from "../../../../theme/sidepanel2/SidepanelHeaderActiongroup";
import Text from "../../../../theme/text/Text";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import { useSelector } from "react-redux";
import BoolSetting from '../../../Settings/BoolSetting';
import Dropdown from "../../../../theme/dropout/DropOut";
import SpDesktopFooter from '../../../../theme/sidepanel2/SidepanelFooter';

const SidePanelActions = (props) => {
    return (
        <SidepanelHeaderActiongroup>
            <Shbutton isCancel={true} 
                onClick={() => props.setIsExtended(false)} 
                color="primary" 
                variant="text" 
                startIcon={<ArrowRightAltIcon/>}
            >
                Cancel
            </Shbutton>

        </SidepanelHeaderActiongroup>
    )
}

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <ButtonGroup version={2}>
            {props.hasModifyPrivilege && (
            <Shbutton
                className={"group-btn"}
                isLoading={props.isLoading}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
            )}
        </ButtonGroup>
    );
};

const renderSettingBox = (label, settingKey, settingValue, options, onChangeHandler) => (
    <Box
        sx={{
            display: "flex",
            gap: "24px 80px",
            flexWrap: "wrap",
            "@media screen and (max-width: 63.9375em)": {
                py: 3,
                flexDirection: "column",
                gap: 4,
                marginBottom: "80px",
            },
        }}
    >
        <Box
            sx={{
                display:"flex",
                gap:2,
                alignItems:"center",
            }}
        >
            <Box 
                sx={{
                    width:280,
                    display:"flex",
                    alignItems:"center",
                }}
            >
                <Text noBreak>{label}</Text>
            </Box>
            <Box display="flex"
                width={1}
                flex={1}
                borderRadius="20px"
                sx={{
                    flex:1,
                }}
            >
                <InputDropdown
                    name={settingKey}
                    displayName="key"
                    selected={options ? options.findIndex(item => item.value === settingValue) ?? -1 : -1}
                    options={options}
                    noSelection={options[0]?.key}
                    onChange={(selected) => onChangeHandler(settingKey, options[selected]?.value)}
                    closeOnSelection
                />
            </Box>
        </Box>
    </Box>
);

const SecondarySettingsBox = ({label, settingKey, settingValue, options, onChangeHandler}) => {
    
    return (
        <Box
            sx={{
                width:1,
                display: "flex",
                gap: "24px 80px",
                flexWrap: "wrap",
                "@media screen and (max-width: 63.9375em)": {
                    py: 3,
                    flexDirection: "column",
                    gap: 4,
                    marginBottom: "80px",
                },
            }}
        >
            <Box
                sx={{
                    width:1,
                    display:"flex",
                    gap:2,
                    alignItems:"center",
                    justifyContent:'space-between',
                }}
            >
                <Box 
                    sx={{
                        width:320,
                        display:"flex",
                        alignItems:"center",
                    }}
                >
                    <Text semibold noBreak>{label}</Text>
                </Box>
                <Box display="flex"
                    // width={1}
                    // flex={1}
                    borderRadius="20px"
                    sx={{
                        // flex:1,
                    }}
                >
                    <InputDropdown
                        name={settingKey}
                        displayName="key"
                        selected={options ? options.findIndex(item => item.value === settingValue) ?? -1 : -1}
                        options={options}
                        noSelection={options[0]?.key}
                        onChange={(selected) => onChangeHandler(settingKey, options[selected]?.value)}
                        closeOnSelection
                    />
                </Box>
            </Box>
        </Box>
    );
}


const Settings = (props) => {
    const  { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const setIsExtendedCallback = useCallback(() => props.setIsExtended(false), [props.setIsExtended]);
    const [settings, setSettings] = useState(null);

    const hasModifyPrivilege = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.RELATION_MANAGE])
    );

    const [open, setOpen] = useState(false);
    
    const { data: incSettings} = useGetSettingsForRelationRawQuery(props.relationId,
        {
            skip: !props.relationId,
        }
    );
    useEffect(() => {
        if(incSettings){
            setSettings(incSettings.map(item => ({ ...item, relationId: props.relationId })));
        }
    },[incSettings]);

    const settingOptions = [
        {key: t("use_global_setting"), value: "unset"},
        {key: t("allow"), value: "true"},
        {key: t("deny"), value: "false"},
    ];
    
    const defaultIcon = useMemo(() => ({
        theme: theme.palette.mode === "dark" ? "blue" : "primary",
        content: <AdminPanelSettingsOutlinedIcon/>
    }), [theme.palette.mode]);

    const defaultState = useMemo(() => ({
        title: "",
        subTitle: "",
        // options: defaultOptions,
        icon: defaultIcon,
        actions:<SidePanelActions setIsExtended={setIsExtendedCallback} />   
    }), [defaultIcon,/*defaultOptions,*/ setIsExtendedCallback]);

    const [panel, setPanel] = useState(defaultState);


    const [updateSettingsForRelation,{isLoading}] = useUpdateSettingsForRelationMutation();

    const handleSaveClick = async () => {
        try {
            if(isLoading) return;
            const response = await updateSettingsForRelation({settings:settings}).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions 
                    id={props.relationId}
                    action={handleSaveClick}
                    isLoading={isLoading}
                    hasModifyPrivilege={hasModifyPrivilege}
                />,
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[props.relationId,handleSaveClick,isLoading,hasModifyPrivilege]);
    
   

    
    const handleChange = (key, value) => {
        setSettings(prevSettings => {
            const updatedSettings = prevSettings.map(setting => 
                setting.key === key ? { ...setting, value } : setting
            );
            if (!updatedSettings.some(setting => setting.key === key)) {
                updatedSettings.push({ key, value, relationId: props.relationId });
            }
            return updatedSettings;
        });
    }
    return (
        <Box
            height={1}
            display={"flex"}
            flexDirection={"column"}
            padding={3}
            sx={{
                pt:0,
                px: 3,
                pb: 11,
            }}
        >
            <Dropdown title={t("pages")} open>
                <Box
                    height={1}
                    display={"flex"}
                    flexDirection={"column"}
                    gap={2}
                    pt={1}
                    
                >
                    {settings && (
                            <>
                                {renderSettingBox(t("page_products"), "page_products", settings.find(setting => setting.key === "page_products")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_orders"), "page_orders", settings.find(setting => setting.key === "page_orders")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_inbound"), "page_inbound", settings.find(setting => setting.key === "page_inbound")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_return"), "page_return", settings.find(setting => setting.key === "page_return")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_invoices"), "page_invoices", settings.find(setting => setting.key === "page_invoices")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_integrations"), "page_integrations", settings.find(setting => setting.key === "page_integrations")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_access"), "page_access", settings.find(setting => setting.key === "page_access")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_suppliers"), "page_suppliers", settings.find(setting => setting.key === "page_suppliers")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("page_servicedesk"), "page_servicedesk", settings.find(setting => setting.key === "page_servicedesk")?.value, settingOptions, handleChange)}
                                {renderSettingBox(t("relation_packaging_usage_export"), "relation_packaging_usage_export", settings.find(setting => setting.key === "relation_packaging_usage_export")?.value, settingOptions, handleChange)}
                                {/* {renderSettingBox(t("relation_edit_product_dimensions"), "relation_edit_product_dimensions", settings.find(setting => setting.key === "relation_edit_product_dimensions")?.value, settingOptions, handleChange)} */}
                                {/* {renderSettingBox(t("relation_import_order"), "relation_import_order", settings.find(setting => setting.key === "relation_import_order")?.value, settingOptions, handleChange)} */}
                        </>
                    )}

                </Box>
            </Dropdown>
            <Dropdown title={t("misc")} open>
                <Box
                height={1}
                display={"flex"}
                flexDirection={"column"}
                gap={2}
                pt={1}
                
                >
                    {settings && (
                        <>
                            
                            <BoolSetting settings={settings} skey="return_portal_available" relationId={props.relationId}/> 
                            <SecondarySettingsBox
                                label={t("relation_edit_product_dimensions")}
                                settingKey={"relation_edit_product_dimensions"}
                                settingValue={settings.find(setting => setting.key === "relation_edit_product_dimensions")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_import_order")}
                                settingKey={"relation_import_order"}
                                settingValue={settings.find(setting => setting.key === "relation_import_order")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_import_inbound")}
                                settingKey={"relation_import_inbound"}
                                settingValue={settings.find(setting => setting.key === "relation_import_inbound")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_allocate_stock_backorders")}
                                settingKey={"relation_allocate_stock_backorders"}
                                settingValue={settings.find(setting => setting.key === "relation_allocate_stock_backorders")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_allow_b2b")}
                                settingKey={"relation_allow_b2b"}
                                settingValue={settings.find(setting => setting.key === "relation_allow_b2b")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_edit_pickingslip")}
                                settingKey={"relation_edit_pickingslip"}
                                settingValue={settings.find(setting => setting.key === "relation_edit_pickingslip")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_edit_product_instructions")}
                                settingKey={"relation_edit_product_instructions"}
                                settingValue={settings.find(setting => setting.key === "relation_edit_product_instructions")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_block_barcode_removal")}
                                settingKey={"relation_block_barcode_removal"}
                                settingValue={settings.find(setting => setting.key === "relation_block_barcode_removal")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                              <SecondarySettingsBox
                                label={t("relation_show_countingstrategies")}
                                settingKey={"relation_show_countingstrategies"}
                                settingValue={settings.find(setting => setting.key === "relation_show_countingstrategies")?.value}
                                options={settingOptions}
                                onChangeHandler={handleChange}
                            />
                        </>
                    )}

                </Box>
            </Dropdown>
            <SpDesktopFooter>
                <GridActions
                    id={props.relationId}
                    action={handleSaveClick}
                    isLoading={isLoading}
                    hasModifyPrivilege={hasModifyPrivilege}
                />

            </SpDesktopFooter>
        </Box>
      );
}

export default Settings;